import { ArdoqId, LinkedComponent } from '@ardoq/api-types';
import { COMPONENT_HIERARCHY_PADDING } from '../consts';
import { zip } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';

/**
 * Per-component / per-reference graph data the CM2 view model needs at
 * augmentation time. The production wrapper pre-resolves these maps
 * from the model interfaces and threads them through the seam so each
 * helper here stays deterministic w.r.t. its inputs.
 */
export type ComponentGraphData = {
  parentIdById: Record<ArdoqId, ArdoqId | null>;
  childrenById: Record<ArdoqId, ArdoqId[]>;
  workspaceIdByComponentId: Record<ArdoqId, ArdoqId | null>;
  /** Global reference-type id keyed by reference id, for the references reachable
   * from the start set only (ARD-32000) - which is every id `getLinkedComponents`
   * can ask about. Readers fall back to `null` for anything else. */
  referenceTypeIdByReferenceId: Record<ArdoqId, string | null>;
  /** Component-type name keyed by component id. Used by
   * `getLinkedComponents` to narrow by `linkedComponentTypeName` when the
   * caller's selection scopes the axis to one connected type
   * (ARD-31884). Components missing from the map collapse to `''`
   * (matches the "unknown" bucket produced by `getReferenceTypes`). */
  componentTypeByComponentId: Record<ArdoqId, string>;
};

export const getComponentHierarchyAddress = (
  componentId: string | undefined,
  parentIdById: Record<ArdoqId, ArdoqId | null>
): (string | null)[] => {
  if (componentId === undefined) {
    return [null];
  }
  const result = [componentId];
  let parentId = parentIdById[componentId] ?? null;

  while (parentId) {
    result.unshift(parentId);
    parentId = parentIdById[parentId] ?? null;
  }
  return result;
};

const getLinkedComponents = (
  componentId: ArdoqId,
  referenceTypeId: string,
  workspaceId: string | undefined,
  linkedComponentsById: Record<ArdoqId, LinkedComponent[]>,
  graphData: Pick<
    ComponentGraphData,
    | 'workspaceIdByComponentId'
    | 'referenceTypeIdByReferenceId'
    | 'componentTypeByComponentId'
  >,
  linkedComponentTypeName?: string
) =>
  (linkedComponentsById[componentId] ?? [])
    .map(linkedComponent => {
      if (
        graphData.workspaceIdByComponentId[linkedComponent.componentId] !==
        workspaceId
      ) {
        return null;
      }
      if (
        linkedComponentTypeName !== undefined &&
        graphData.componentTypeByComponentId[linkedComponent.componentId] !==
          linkedComponentTypeName
      ) {
        return null;
      }
      const linkedComponentReferenceTypeId =
        graphData.referenceTypeIdByReferenceId[linkedComponent.referenceId] ??
        null;
      return linkedComponentReferenceTypeId === referenceTypeId
        ? {
            componentId: linkedComponent.componentId,
            referenceTypeId: linkedComponentReferenceTypeId,
          }
        : null;
    })
    .filter(isTruthy);

const getReferenceAddressWithComponentHierarchy = (
  referencedComponentId: string | undefined,
  referenceTypeId: string | undefined,
  parentIdById: Record<ArdoqId, ArdoqId | null>,
  componentHierarchyMaxDepth: number = Number.NEGATIVE_INFINITY
) => {
  if (!referenceTypeId) {
    return [];
  }
  const componentHierarchyAddress = getComponentHierarchyAddress(
    referencedComponentId,
    parentIdById
  );
  return componentHierarchyAddress.length >= componentHierarchyMaxDepth
    ? componentHierarchyAddress
    : componentHierarchyAddress.concat(
        Array(
          componentHierarchyMaxDepth - componentHierarchyAddress.length
        ).fill(COMPONENT_HIERARCHY_PADDING)
      );
};

export const getLinkedComponentsAddresses = (
  componentId: string,
  referenceTypeId: string,
  workspaceId: string,
  linkedComponentsById: Record<ArdoqId, LinkedComponent[]>,
  graphData: ComponentGraphData,
  includeComponentHierarchy = true,
  componentHierarchyMaxDepth?: number,
  linkedComponentTypeName?: string
): (string | null)[][] => {
  const linkedComponents = getLinkedComponents(
    componentId,
    referenceTypeId,
    workspaceId,
    linkedComponentsById,
    graphData,
    linkedComponentTypeName
  );

  const actualResult = includeComponentHierarchy
    ? linkedComponents.length
      ? linkedComponents.map(linkedComponent =>
          getReferenceAddressWithComponentHierarchy(
            linkedComponent.componentId,
            linkedComponent.referenceTypeId,
            graphData.parentIdById,
            componentHierarchyMaxDepth
          )
        )
      : [
          [null].concat(
            Array((componentHierarchyMaxDepth || 1) - 1).fill(
              COMPONENT_HIERARCHY_PADDING
            )
          ),
        ]
    : linkedComponents.length
      ? (zip(
          linkedComponents.map(linkedComponent => linkedComponent.componentId)
        ) as (string | null)[][])
      : [[null]];
  return actualResult;
};

export const getParentAddresses = (
  componentId: ArdoqId,
  parentIdById: Record<ArdoqId, ArdoqId | null>
): (string | null)[][] => {
  const parentId = parentIdById[componentId] ?? null;
  if (!parentId) return [[null]];
  return [getComponentHierarchyAddress(parentId, parentIdById)];
};

export const expandAddressWithChildren = (
  baseAddr: (string | null)[],
  anchorId: string,
  childDegrees: number,
  childrenById: Record<ArdoqId, ArdoqId[]>
): (string | null)[][] => {
  if (childDegrees <= 0) return [];
  const children = childrenById[anchorId] ?? [];
  return children.flatMap(childId => {
    const childAddr = [...baseAddr, childId];
    return [
      childAddr,
      ...expandAddressWithChildren(
        childAddr,
        childId,
        childDegrees - 1,
        childrenById
      ),
    ];
  });
};
